import { useEffect, useState, useRef } from "react";
import PaginationTable from "../pagination/Pagination4";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const VerifyFPSWiseCommissionNFSAReportDetails = ({
  month,
  year,
  allotMonth,
  allotYear,
  commId,
  distCode,
  reportTitle,
}) => {
  const [commissionData, setCommissionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchFPSNFSACommissionData = async () => {
      try {
        const queryParams = new URLSearchParams({
          month,
          year,
          commId,
          allotMonth,
          allotYear,
          distCode,
        });

        const response = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/verify-nfsa-fps-commission?${queryParams}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch FPS NFSA commission data");
        }

        const data = await response.json();
        setApiData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchFPSNFSACommissionData();
  }, [month, year, allotMonth, allotYear, commId, distCode, reportTitle]);

  useEffect(() => {
    const fetCommissionCost = async () => {
      try {
        const commissioncostresponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/commission-cost`
        );

        if (!commissioncostresponse.ok) {
          throw new Error("Failed to fetch State commission data");
        }

        const data = await commissioncostresponse.json();
        setCommissionData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      }
    };
    fetCommissionCost();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value === "all" ? apiData.length : parseInt(value, 10));
    setPage(0);
  };

  const filteredData = apiData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) => value && value.toString().toLowerCase().includes(query)
    );
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }



  const totals = filteredData.reduce(
    (acc, item) => {
      acc.shopTotalQty += Number(item.shopTotalQty) || 0;
      acc.nfsaAmount += Number(item.nfsaAmount) || 0;
      acc.impdsTransCount += Number(item.impdsTransCount) || 0;
      acc.impdsTotalQty += Number(item.impdsTotalQty) || 0;
      acc.amountRecovered += Number(item.amountRecovered) || 0;
      acc.finalCommission += Number(item.finalCommission) || 0;
      acc.count += 1;
      return acc;
    },
    {
      shopTotalQty: 0,
      nfsaAmount: 0,
      impdsTransCount: 0,
      impdsTotalQty: 0,
      amountRecovered: 0,
      finalCommission: 0,
      count: 0,
    }
  );


  const handleDownloadExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Verify NFSA Commission');

    const totalColumns = 16;


    const totals = data.reduce(
      (acc, item) => {
        acc.shopTotalQty += Number(item.shopTotalQty) || 0;
        acc.nfsaAmount += Number(item.nfsaAmount) || 0;
        acc.impdsTransCount += Number(item.impdsTransCount) || 0;
        acc.impdsTotalQty += Number(item.impdsTotalQty) || 0;
        acc.amountRecovered += Number(item.amountRecovered) || 0;
        acc.finalCommission += Number(item.finalCommission) || 0;
        return acc;
      },
      {
        shopTotalQty: 0,
        nfsaAmount: 0,
        impdsTransCount: 0,
        impdsTotalQty: 0,
        amountRecovered: 0,
        finalCommission: 0,
      }
    );


    worksheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = worksheet.getCell('A1');
    titleCell.value = reportTitle;
    titleCell.font = { size: 12, bold: true };
    titleCell.alignment = { horizontal: 'center' };


    const headers = [
      "S.No", "District", "Office / Mandal", "Shop No.", "FPS Dealer Name", "Bank Name",
      "Branch Name", "IFSC Code", "PAN No", "Account No", "Total Quantity Distributed (in kg) (A)",
      `Amount Payable Towards Commission(Rs. ${commissionData.nfsaCost || 0} /Kg) (B=A * ${commissionData.nfsaCost || 0} )`, "Total IMPDS Transaction", "Total Quantity Distributed under IMPDS (in kg)", "Amount Recovered (C)",
      "Net Amount Payable (Rs.)(D=B-C)"
    ];

    worksheet.addRow(headers);

    const headerRow = worksheet.getRow(2);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center' };
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDDDDD' } };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });

    data.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        item.distNameEn,
        item.afsoNameEn,
        item.fpsId,
        item.dealerName,
        item.bankName,
        item.branchName,
        item.ifscCode,
        item.panNo,
        item.acNo,
        Number(item.shopTotalQty).toFixed(3),
        Number(item.nfsaAmount).toFixed(2),
        item.impdsTransCount,
        Number(item.impdsTotalQty).toFixed(3),
        item.amountRecovered,
        Number(item.finalCommission).toFixed(2)
      ]);

      for (let i = 1; i <= 10; i++) row.getCell(i).alignment = { horizontal: 'left' };
      for (let i = 11; i <= 16; i++) row.getCell(i).alignment = { horizontal: 'right' };
    });


    const dataTime = data[0]?.dataTime
      ? data[0].dataTime.substring(0, 18).replace("T", " ")
      : "--";

    const dataTimeRowNum = worksheet.lastRow.number + 1;

    worksheet.mergeCells(dataTimeRowNum, 1, dataTimeRowNum, totalColumns);

    const dataTimeRow = worksheet.getRow(dataTimeRowNum);
    dataTimeRow.getCell(1).value = `Data Time: ${dataTime}`;
    dataTimeRow.font = { bold: true, color: { argb: 'FFFF0000' } }; // red text
    dataTimeRow.alignment = { horizontal: 'right' };

    dataTimeRow.commit();


    const totalRowNumber = worksheet.lastRow.number + 1;

    worksheet.mergeCells(totalRowNumber, 1, totalRowNumber, 10);

    const totalRow = worksheet.addRow([
      '', '', '', '', '', '', '', '', '', 'Total',
      totals.shopTotalQty.toFixed(3),
      totals.nfsaAmount.toFixed(2),
      totals.impdsTransCount,
      totals.impdsTotalQty.toFixed(3),
      totals.amountRecovered,
      totals.finalCommission.toFixed(2),
    ]);

    totalRow.font = { bold: true };
    totalRow.alignment = { horizontal: 'right' };
    totalRow.getCell(1).alignment = { horizontal: 'left' };

    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'double' },
        right: { style: 'thin' }
      };
      if (colNumber <= 10) cell.alignment = { horizontal: 'left' };
    });


    worksheet.columns.forEach((col, index) => {
      let maxLength = 10;
      const headerText = headers[index] || '';
      maxLength = Math.max(maxLength, headerText.length);

      col.eachCell({ includeEmpty: true }, (cell) => {
        const val = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, val.length);
      });

      col.width = Math.min(maxLength + 2, 30);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }), `Verify_NFSA_Wise_Commission.xlsx`);
  };

  if (error) {
    return (
      <div className="mainhead" align="center">
        Internal error
      </div>
    );
  }

  if (!loading && apiData.length === 0) {
    return (
      <div className="mainhead" align="center">
        No data found
      </div>
    );
  }


  return (
    <div style={{ justifyContent: "center", marginBottom: "70px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "90%",
          margin: "0 auto 20px auto",
        }}
      >
        {<div style={{ width: "150px" }}></div>}

        <div style={{ align: "center" }}>
          <PaginationTable
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>

        <div>
          <input
            type="text"
            placeholder="Search "
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: "8px", width: "200px" }}
          />
        </div>
      </div>

      <table width="90%" border="1" cellPadding="5" cellSpacing="0">
        <thead style={{ position: "sticky", top: 0 }}>
          <tr className="tableheader">
            <th colSpan={16}>{reportTitle}</th>
          </tr>
          <tr className="tableheader">
            <th>S.No</th>
            <th>District</th>
            <th>Office / Mandal</th>
            <th>Shop No.</th>
            <th>FPS Dealer Name</th>
            <th>Bank Name</th>
            <th>Branch Name</th>
            <th>IFSC Code</th>
            <th>PAN No</th>
            <th>Account No</th>
            <th>
              Total Quantity Distributed (in kg) <br />
              <font style={{ color: "orange" }}>(A)</font>
            </th>
            <th>
              Amount Payable Towards Commission(Rs. {commissionData.nfsaCost}
              /Kg) <br />
              <font style={{ color: "orange" }}>
                (B=A * {commissionData.nfsaCost})
              </font>
            </th>
            <th>Total IMPDS Transaction</th>
            <th>Total Quantity Distributed under IMPDS (in kg)</th>
            <th>
              Amount Recovered <br />
              <font style={{ color: "orange" }}>(C)</font>
            </th>
            <th>
              Net Amount Payable (Rs.) <br />
              <font style={{ color: "orange" }}>(D=B-C)</font>
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={index}>
              <td>
                {rowsPerPage === "all"
                  ? index + 1
                  : index + 1 + page * rowsPerPage}
              </td>
              <td>{item.distNameEn}</td>
              <td>{item.afsoNameEn}</td>
              <td>{item.fpsId}</td>
              <td>{item.dealerName}</td>
              <td>{item.bankName}</td>
              <td>{item.branchName}</td>
              <td>{item.ifscCode}</td>
              <td>{item.panNo}</td>
              <td>{item.acNo}</td>
              <td style={{ textAlign: "right" }}>
                {item.shopTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.nfsaAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>{item.impdsTransCount}</td>
              <td style={{ textAlign: "right" }}>
                {item.impdsTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>{item.amountRecovered}</td>
              <td style={{ textAlign: "right" }}>
                {item.finalCommission.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: "bold" }} className="tableheader">
            <td colSpan={10} style={{ textAlign: "center" }}>
              Total
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.shopTotalQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.nfsaAmount.toFixed(2)}
            </td>
            <td style={{ textAlign: "right" }}>{totals.impdsTransCount}</td>
            <td style={{ textAlign: "right" }}>
              {totals.impdsTotalQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.amountRecovered.toFixed(2)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.finalCommission.toFixed(2)}
            </td>
          </tr>
          <tr>
            <td colSpan={19} style={{ textAlign: "right" }}>
              Data Time:{" "}
              <font style={{ color: "red" }}>
                {paginatedData[0]?.dataTime
                  ?.substring(0, 18)
                  ?.replace("T", " ") || "--"}
              </font>
            </td>
          </tr>
        </tfoot>
      </table>

      {<div style={{ marginTop: "20px", textAlign: "center" }}>
        <button
          onClick={() => handleDownloadExcel(apiData)}
          className="button button2"
        >
          Download Excel
        </button>
      </div>}

      <div style={{ marginTop: "20px", color: "red", fontWeight: "600", fontSize: "21px" }}>
        Note: Profession Tax and TDS will be calculated after commission data generation.
      </div>

    </div>
  );
};

export default VerifyFPSWiseCommissionNFSAReportDetails;
